import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { LogIn, LogOut, Search, Settings } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { OrgAuthApi, OrgPortalApi } from '@/services/org.api';
import type { School } from '@/services/superadmin.api';
import { DataTable, useDataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { toast } from '@/stores/toast.store';

const statusTone: Record<
  School['status'],
  'green' | 'amber' | 'red' | 'slate' | 'blue'
> = {
  trial: 'amber',
  active: 'green',
  grace_period: 'amber',
  suspended: 'red',
  cancelled: 'slate',
};

const columnHelper = createColumnHelper<School>();

export function OrgSelectSchoolPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const logout = useAuthStore((s) => s.logout);
  const orgToken = useAuthStore((s) => s.orgToken);
  const enterSchoolSession = useAuthStore((s) => s.enterSchoolSession);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  // Academic-group nav items deep-link here with ?next=/setup/classes etc. —
  // same "enter a school" flow, just landing somewhere other than /dashboard.
  const next = searchParams.get('next') || '/dashboard';

  const { data: org } = useQuery({ queryKey: ['org-me'], queryFn: OrgPortalApi.me });

  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });

  const { data, isFetching } = useQuery({
    queryKey: ['org-select-school', dt.params],
    queryFn: () => OrgAuthApi.myOrgSchools(dt.params, orgToken ?? ''),
    enabled: !!orgToken,
    placeholderData: keepPreviousData,
  });

  const enter = useMutation({
    mutationFn: (schoolId: string) =>
      OrgAuthApi.switchSchool(schoolId, orgToken ?? ''),
    onMutate: (schoolId) => setBusyId(schoolId),
    onSuccess: (session) => {
      enterSchoolSession({
        user: {
          id: session.user.id,
          name: session.user.name,
          email: session.user.email,
          role: session.user.role,
          schoolId: session.user.schoolId,
          schoolSlug: session.user.schoolSlug,
          scope: 'school',
          isSchemaProvisioned: session.school.isSchemaProvisioned,
        },
        accessToken: session.tokens.accessToken,
        refreshToken: session.tokens.refreshToken,
        schoolSlug: session.school.slug,
      });
      qc.clear();
      navigate(next);
    },
    onError: (e: any) =>
      toast.error(
        e?.response?.data?.error?.message ?? e?.message ?? 'Could not enter school',
      ),
    onSettled: () => setBusyId(null),
  });

  // Admin accounts tied to exactly one school (the common case for a
  // "school admin" whose login happens to resolve to an org-shaped,
  // multi-school-capable session) shouldn't have to click through a
  // picker that only ever has one row — auto-enter it, same as manually
  // clicking "Enter" on that single row. Guarded on an unfiltered first
  // page so a search result that happens to narrow to one match never
  // triggers this.
  const autoEnteredRef = useRef(false);
  useEffect(() => {
    if (autoEnteredRef.current) return;
    if (dt.params.search || dt.params.page !== 1) return;
    if (!data || data.total !== 1 || !data.items[0]) return;
    autoEnteredRef.current = true;
    enter.mutate(data.items[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        enableSorting: true,
        cell: (info) => (
          <div className="leading-tight">
            <div className="font-medium text-slate-900">{info.getValue()}</div>
            <code className="text-xs text-slate-500">{info.row.original.slug}</code>
          </div>
        ),
      }),
      columnHelper.accessor('code', {
        id: 'code',
        header: 'Code',
        enableSorting: true,
        cell: (info) => (
          <span className="font-mono text-xs">{info.getValue() ?? '—'}</span>
        ),
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        enableSorting: true,
        cell: (info) => (
          <Badge tone={statusTone[info.getValue()] ?? 'slate'}>{info.getValue()}</Badge>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => (
          <button
            className="btn-secondary inline-flex items-center gap-1.5 py-1 text-xs"
            onClick={() => enter.mutate(info.row.original.id)}
            disabled={busyId === info.row.original.id}
          >
            <LogIn className="h-3.5 w-3.5" />
            {busyId === info.row.original.id ? 'Entering…' : 'Enter'}
          </button>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyId],
  );

  // Skip the picker UI entirely while the single-school auto-enter above is
  // in flight — otherwise the full table flashes for a frame before
  // navigating away.
  if (autoEnteredRef.current && (enter.isPending || enter.isSuccess)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-500">
        Entering your school…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">
              {org?.name ?? 'Organization'}
            </h1>
            <p className="text-xs text-slate-500">
              {next === '/dashboard' ? 'Choose a school to enter' : `Choose a school to open ${next}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              onClick={() => navigate('/org/schools')}
            >
              <Settings className="h-4 w-4" /> Manage schools
            </button>
            <button
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              onClick={() => {
                logout();
                navigate('/org/login');
              }}
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 py-6">
        <DataTable
          columns={columns}
          result={data}
          state={dt}
          loading={isFetching}
          emptyMessage={
            dt.params.search
              ? 'No schools match your search.'
              : 'No schools in this organization yet.'
          }
          toolbar={
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search schools…"
                className="pl-9"
              />
            </div>
          }
        />
      </div>
    </div>
  );
}
